import React, { useState, useEffect } from 'react';
import { Package, Globe, Send, ArrowRight, Filter, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Products = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const categoryParam = searchParams.get('category');
  
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(categoryParam || null);
  const [isEnquiryOpen, setIsEnquiryOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [enquiryForm, setEnquiryForm] = useState({
    full_name: '',
    company_name: '',
    email: '',
    country: '',
    phone: '',
    quantity: '',
    message: ''
  });

  const fallbackCategories = [
    {
      category_id: "cat-spices",
      name: "Spices & Seasonings",
      slug: "spices-seasonings",
      description: "Premium Indian spices and seasonings for global cuisines",
      image_url: "https://images.unsplash.com/photo-1596040033229-a9821ebd058d?w=600&h=400&fit=crop&q=80"
    },
    {
      category_id: "cat-pulses",
      name: "Pulses, Grains & Seeds",
      slug: "pulses-grains-seeds",
      description: "Quality pulses, grains and seeds from Indian farms",
      image_url: "https://images.unsplash.com/photo-1586201375761-83865001e31c?w=600&h=400&fit=crop&q=80"
    },
    {
      category_id: "cat-fruits",
      name: "Fresh Fruits & Vegetables",
      slug: "fresh-fruits-vegetables",
      description: "Farm-fresh fruits and vegetables from India",
      image_url: "https://images.unsplash.com/photo-1610832958506-aa56368176cf?w=600&h=400&fit=crop&q=80"
    },
    {
      category_id: "cat-fmcg",
      name: "FMCG & Packaged Foods",
      slug: "fmcg-packaged-foods",
      description: "Ready-to-eat and packaged Indian food products",
      image_url: "https://images.unsplash.com/photo-1604719312566-8912e9227c6a?w=600&h=400&fit=crop&q=80"
    },
    {
      category_id: "cat-dryfruits",
      name: "Dry Fruits, Nuts & Dehydrated Foods",
      slug: "dry-fruits-nuts",
      description: "Premium dry fruits, nuts and dehydrated food products",
      image_url: "https://images.unsplash.com/photo-1599599810769-bcde5a160d32?w=600&h=400&fit=crop&q=80"
    },
    {
      category_id: "cat-oils",
      name: "Edible Oils, Sweeteners & Staples",
      slug: "edible-oils-sweeteners",
      description: "Quality edible oils, jaggery, sugar and food staples",
      image_url: "https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?w=600&h=400&fit=crop&q=80"
    },
    {
      category_id: "cat-hardware",
      name: "Hardware & Kitchenware",
      slug: "hardware-kitchenware",
      description: "Stainless steel utensils, cookware and hardware products",
      image_url: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=600&h=400&fit=crop&q=80"
    }
  ];

  const fallbackProducts = {
    "cat-spices": [
      { product_id: "1", name: "Turmeric Powder", image_url: "https://images.unsplash.com/photo-1615485500704-8e990f9900f7?w=400&h=300&fit=crop&q=80" },
      { product_id: "2", name: "Red Chilli Powder", image_url: "https://images.unsplash.com/photo-1599909533681-74084252c9d5?w=400&h=300&fit=crop&q=80" },
      { product_id: "3", name: "Whole Red Chillies", image_url: "https://images.unsplash.com/photo-1583119022894-919a68a3d0e3?w=400&h=300&fit=crop&q=80" },
      { product_id: "4", name: "Cumin Seeds", image_url: "https://images.unsplash.com/photo-1599909533681-74084252c9d5?w=400&h=300&fit=crop&q=80" },
      { product_id: "5", name: "Coriander Seeds", image_url: "https://images.unsplash.com/photo-1596040033229-a9821ebd058d?w=400&h=300&fit=crop&q=80" },
      { product_id: "6", name: "Mustard Seeds", image_url: "https://images.unsplash.com/photo-1596040033229-a9821ebd058d?w=400&h=300&fit=crop&q=80" },
      { product_id: "7", name: "Black Pepper", image_url: "https://images.unsplash.com/photo-1599909533681-74084252c9d5?w=400&h=300&fit=crop&q=80" },
      { product_id: "8", name: "Cinnamon", image_url: "https://images.unsplash.com/photo-1596040033229-a9821ebd058d?w=400&h=300&fit=crop&q=80" },
      { product_id: "9", name: "Cardamom", image_url: "https://images.unsplash.com/photo-1596040033229-a9821ebd058d?w=400&h=300&fit=crop&q=80" },
      { product_id: "10", name: "Blended Spices", image_url: "https://images.unsplash.com/photo-1596040033229-a9821ebd058d?w=400&h=300&fit=crop&q=80" }
    ],
    "cat-pulses": [
      { product_id: "11", name: "Toor Dal", image_url: "https://images.unsplash.com/photo-1586201375761-83865001e31c?w=400&h=300&fit=crop&q=80" },
      { product_id: "12", name: "Chana Dal", image_url: "https://images.unsplash.com/photo-1586201375761-83865001e31c?w=400&h=300&fit=crop&q=80" },
      { product_id: "13", name: "Moong Dal", image_url: "https://images.unsplash.com/photo-1586201375761-83865001e31c?w=400&h=300&fit=crop&q=80" },
      { product_id: "14", name: "Masoor Dal", image_url: "https://images.unsplash.com/photo-1586201375761-83865001e31c?w=400&h=300&fit=crop&q=80" },
      { product_id: "15", name: "Urad Dal", image_url: "https://images.unsplash.com/photo-1586201375761-83865001e31c?w=400&h=300&fit=crop&q=80" },
      { product_id: "16", name: "Chickpeas", image_url: "https://images.unsplash.com/photo-1586201375761-83865001e31c?w=400&h=300&fit=crop&q=80" },
      { product_id: "17", name: "Basmati Rice", image_url: "https://images.unsplash.com/photo-1586201375761-83865001e31c?w=400&h=300&fit=crop&q=80" },
      { product_id: "18", name: "Non-Basmati Rice", image_url: "https://images.unsplash.com/photo-1586201375761-83865001e31c?w=400&h=300&fit=crop&q=80" },
      { product_id: "19", name: "Wheat", image_url: "https://images.unsplash.com/photo-1586201375761-83865001e31c?w=400&h=300&fit=crop&q=80" },
      { product_id: "20", name: "Maize", image_url: "https://images.unsplash.com/photo-1586201375761-83865001e31c?w=400&h=300&fit=crop&q=80" },
      { product_id: "21", name: "Sesame Seeds", image_url: "https://images.unsplash.com/photo-1586201375761-83865001e31c?w=400&h=300&fit=crop&q=80" },
      { product_id: "22", name: "Groundnuts", image_url: "https://images.unsplash.com/photo-1586201375761-83865001e31c?w=400&h=300&fit=crop&q=80" },
      { product_id: "23", name: "Other Oilseeds", image_url: "https://images.unsplash.com/photo-1586201375761-83865001e31c?w=400&h=300&fit=crop&q=80" }
    ],
    "cat-fruits": [
      { product_id: "24", name: "Onions", image_url: "https://images.unsplash.com/photo-1618512496248-a07fe83aa8cb?w=400&h=300&fit=crop&q=80" },
      { product_id: "25", name: "Garlic", image_url: "https://images.unsplash.com/photo-1540148426945-6cf22a6b2f8c?w=400&h=300&fit=crop&q=80" },
      { product_id: "26", name: "Potatoes", image_url: "https://images.unsplash.com/photo-1518977676601-b53f82ber3a?w=400&h=300&fit=crop&q=80" },
      { product_id: "27", name: "Fresh Green Chillies", image_url: "https://images.unsplash.com/photo-1583119022894-919a68a3d0e3?w=400&h=300&fit=crop&q=80" },
      { product_id: "28", name: "Tomatoes", image_url: "https://images.unsplash.com/photo-1546470427-227c7369a9b8?w=400&h=300&fit=crop&q=80" },
      { product_id: "29", name: "Mangoes", image_url: "https://images.unsplash.com/photo-1553279768-865429fa0078?w=400&h=300&fit=crop&q=80" },
      { product_id: "30", name: "Bananas", image_url: "https://images.unsplash.com/photo-1571771894821-ce9b6c11b08e?w=400&h=300&fit=crop&q=80" },
      { product_id: "31", name: "Grapes", image_url: "https://images.unsplash.com/photo-1537640538966-79f369143f8f?w=400&h=300&fit=crop&q=80" },
      { product_id: "32", name: "Pomegranates", image_url: "https://images.unsplash.com/photo-1541344999736-83eca272f6fc?w=400&h=300&fit=crop&q=80" },
      { product_id: "33", name: "Other Seasonal Fruits", image_url: "https://images.unsplash.com/photo-1610832958506-aa56368176cf?w=400&h=300&fit=crop&q=80" },
      { product_id: "34", name: "Other Seasonal Vegetables", image_url: "https://images.unsplash.com/photo-1610832958506-aa56368176cf?w=400&h=300&fit=crop&q=80" }
    ],
    "cat-fmcg": [
      { product_id: "35", name: "Namkeen", image_url: "https://images.unsplash.com/photo-1604719312566-8912e9227c6a?w=400&h=300&fit=crop&q=80" },
      { product_id: "36", name: "Snacks", image_url: "https://images.unsplash.com/photo-1604719312566-8912e9227c6a?w=400&h=300&fit=crop&q=80" },
      { product_id: "37", name: "Biscuits", image_url: "https://images.unsplash.com/photo-1604719312566-8912e9227c6a?w=400&h=300&fit=crop&q=80" },
      { product_id: "38", name: "Papad", image_url: "https://images.unsplash.com/photo-1604719312566-8912e9227c6a?w=400&h=300&fit=crop&q=80" },
      { product_id: "39", name: "Pickles", image_url: "https://images.unsplash.com/photo-1604719312566-8912e9227c6a?w=400&h=300&fit=crop&q=80" },
      { product_id: "40", name: "Sauces", image_url: "https://images.unsplash.com/photo-1604719312566-8912e9227c6a?w=400&h=300&fit=crop&q=80" },
      { product_id: "41", name: "Ready-to-Cook Foods", image_url: "https://images.unsplash.com/photo-1604719312566-8912e9227c6a?w=400&h=300&fit=crop&q=80" },
      { product_id: "42", name: "Instant Mixes", image_url: "https://images.unsplash.com/photo-1604719312566-8912e9227c6a?w=400&h=300&fit=crop&q=80" },
      { product_id: "43", name: "Confectionery", image_url: "https://images.unsplash.com/photo-1604719312566-8912e9227c6a?w=400&h=300&fit=crop&q=80" },
      { product_id: "44", name: "Packaged Indian Grocery Products", image_url: "https://images.unsplash.com/photo-1604719312566-8912e9227c6a?w=400&h=300&fit=crop&q=80" }
    ],
    "cat-dryfruits": [
      { product_id: "45", name: "Raisins", image_url: "https://images.unsplash.com/photo-1599599810769-bcde5a160d32?w=400&h=300&fit=crop&q=80" },
      { product_id: "46", name: "Cashews", image_url: "https://images.unsplash.com/photo-1599599810769-bcde5a160d32?w=400&h=300&fit=crop&q=80" },
      { product_id: "47", name: "Almonds", image_url: "https://images.unsplash.com/photo-1599599810769-bcde5a160d32?w=400&h=300&fit=crop&q=80" },
      { product_id: "48", name: "Dates", image_url: "https://images.unsplash.com/photo-1599599810769-bcde5a160d32?w=400&h=300&fit=crop&q=80" },
      { product_id: "49", name: "Pistachios", image_url: "https://images.unsplash.com/photo-1599599810769-bcde5a160d32?w=400&h=300&fit=crop&q=80" },
      { product_id: "50", name: "Makhana", image_url: "https://images.unsplash.com/photo-1599599810769-bcde5a160d32?w=400&h=300&fit=crop&q=80" },
      { product_id: "51", name: "Dried Chillies", image_url: "https://images.unsplash.com/photo-1599599810769-bcde5a160d32?w=400&h=300&fit=crop&q=80" },
      { product_id: "52", name: "Dehydrated Onion", image_url: "https://images.unsplash.com/photo-1599599810769-bcde5a160d32?w=400&h=300&fit=crop&q=80" },
      { product_id: "53", name: "Dehydrated Garlic", image_url: "https://images.unsplash.com/photo-1599599810769-bcde5a160d32?w=400&h=300&fit=crop&q=80" },
      { product_id: "54", name: "Other Dehydrated Food Products", image_url: "https://images.unsplash.com/photo-1599599810769-bcde5a160d32?w=400&h=300&fit=crop&q=80" }
    ],
    "cat-oils": [
      { product_id: "55", name: "Jaggery", image_url: "https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?w=400&h=300&fit=crop&q=80" },
      { product_id: "56", name: "Sugar", image_url: "https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?w=400&h=300&fit=crop&q=80" },
      { product_id: "57", name: "Groundnut Oil", image_url: "https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?w=400&h=300&fit=crop&q=80" },
      { product_id: "58", name: "Sesame Oil", image_url: "https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?w=400&h=300&fit=crop&q=80" },
      { product_id: "59", name: "Sunflower Oil", image_url: "https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?w=400&h=300&fit=crop&q=80" },
      { product_id: "60", name: "Other Edible Oils", image_url: "https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?w=400&h=300&fit=crop&q=80" },
      { product_id: "61", name: "Other Food Staples", image_url: "https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?w=400&h=300&fit=crop&q=80" }
    ],
    "cat-hardware": [
      { product_id: "62", name: "Stainless Steel Utensils", image_url: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&h=300&fit=crop&q=80" },
      { product_id: "63", name: "Cookware", image_url: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&h=300&fit=crop&q=80" },
      { product_id: "64", name: "Kitchen Tools", image_url: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&h=300&fit=crop&q=80" },
      { product_id: "65", name: "Kitchen Storage Containers", image_url: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&h=300&fit=crop&q=80" },
      { product_id: "66", name: "Fasteners", image_url: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&h=300&fit=crop&q=80" },
      { product_id: "67", name: "Hand Tools", image_url: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&h=300&fit=crop&q=80" },
      { product_id: "68", name: "General Hardware Products", image_url: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&h=300&fit=crop&q=80" }
    ]
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (categoryParam) {
      // Find category by slug or id
      const cat = categories.find(c => c.slug === categoryParam || c.category_id === categoryParam);
      if (cat) {
        setSelectedCategory(cat.category_id);
      }
    }
  }, [categoryParam, categories]);

  const fetchData = async () => {
    try {
      // Fetch categories
      let categoriesData = [];
      try {
        const catResponse = await axios.get(`${BACKEND_URL}/api/categories`);
        categoriesData = catResponse.data.categories || [];
        
        if (categoriesData.length === 0) {
          // Seed if empty
          await axios.post(`${BACKEND_URL}/api/seed-categories`);
          const retryResponse = await axios.get(`${BACKEND_URL}/api/categories`);
          categoriesData = retryResponse.data.categories || [];
        }
      } catch (e) {
        console.error('Using fallback categories');
        categoriesData = fallbackCategories;
      }
      setCategories(categoriesData.length > 0 ? categoriesData : fallbackCategories);

      // Fetch products
      try {
        const prodResponse = await axios.get(`${BACKEND_URL}/api/products`);
        setProducts(prodResponse.data.products || []);
      } catch (e) {
        console.error('Using fallback products');
        setProducts([]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setCategories(fallbackCategories);
    } finally {
      setLoading(false);
    }
  };

  const openEnquiryForm = (product) => {
    setSelectedProduct(product);
    setIsEnquiryOpen(true);
  };

  const handleEnquirySubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const productInfo = selectedProduct ? `Product: ${selectedProduct.name}` : 'General Enquiry';
      await axios.post(`${BACKEND_URL}/api/contact`, {
        full_name: enquiryForm.full_name,
        email: enquiryForm.email,
        mobile: enquiryForm.phone,
        company_name: enquiryForm.company_name,
        message: `${productInfo}\nCountry: ${enquiryForm.country}\nQuantity Required: ${enquiryForm.quantity}\n\nMessage: ${enquiryForm.message}`
      });
      toast.success('Enquiry submitted successfully! We will contact you soon.');
      setIsEnquiryOpen(false);
      setEnquiryForm({ full_name: '', company_name: '', email: '', country: '', phone: '', quantity: '', message: '' });
      setSelectedProduct(null);
    } catch (error) {
      toast.error('Failed to submit enquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getProductsForCategory = (categoryId) => {
    const dbProducts = products.filter(p => p.category_id === categoryId);
    if (dbProducts.length > 0) return dbProducts;
    return fallbackProducts[categoryId] || [];
  };

  const displayCategories = categories.length > 0 ? categories : fallbackCategories;

  const markets = [
    { name: 'United Arab Emirates (UAE)', code: 'ae' },
    { name: 'Saudi Arabia', code: 'sa' },
    { name: 'Qatar', code: 'qa' },
    { name: 'Kuwait', code: 'kw' },
    { name: 'Oman', code: 'om' },
    { name: 'Bahrain', code: 'bh' },
    { name: 'United States of America (USA)', code: 'us' },
    { name: 'United Kingdom (UK)', code: 'gb' },
    { name: 'Canada', code: 'ca' },
    { name: 'Germany', code: 'de' },
    { name: 'Netherlands', code: 'nl' },
    { name: 'Italy', code: 'it' },
    { name: 'France', code: 'fr' },
    { name: 'Spain', code: 'es' },
    { name: 'Australia', code: 'au' },
    { name: 'New Zealand', code: 'nz' },
    { name: 'Singapore', code: 'sg' },
    { name: 'Malaysia', code: 'my' },
    { name: 'Indonesia', code: 'id' },
    { name: 'Thailand', code: 'th' },
    { name: 'South Africa', code: 'za' },
    { name: 'Nigeria', code: 'ng' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="py-16 relative"
        style={{
          backgroundImage: `linear-gradient(rgba(11, 44, 77, 0.9), rgba(11, 44, 77, 0.9)), url('https://images.unsplash.com/photo-1596040033229-a9821ebd058d?w=1920')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="container mx-auto px-6 text-center text-white">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Our Products & Categories</h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            GoldenPort exports premium quality Indian products across 7 major categories to global markets. 
            Product availability is subject to sourcing and confirmation.
          </p>
        </div>
      </section>

      {/* Category Navigation */}
      <section className="py-8 bg-gray-50 sticky top-[76px] z-40 shadow-sm">
        <div className="container mx-auto px-6">
          <div className="flex items-center gap-4 overflow-x-auto pb-2">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`px-4 py-2 rounded-full whitespace-nowrap font-medium transition-all ${
                !selectedCategory 
                  ? 'bg-[#0B2C4D] text-white' 
                  : 'bg-white text-[#0B2C4D] hover:bg-gray-100 border border-gray-200'
              }`}
            >
              All Categories
            </button>
            {displayCategories.map((cat) => (
              <button
                key={cat.category_id}
                onClick={() => setSelectedCategory(cat.category_id)}
                className={`px-4 py-2 rounded-full whitespace-nowrap font-medium transition-all ${
                  selectedCategory === cat.category_id 
                    ? 'bg-[#C9A24D] text-[#0B2C4D]' 
                    : 'bg-white text-[#0B2C4D] hover:bg-gray-100 border border-gray-200'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Categories & Products Section */}
      <section className="py-12">
        <div className="container mx-auto px-6">
          {loading ? (
            <div className="text-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C9A24D] mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading products...</p>
            </div>
          ) : (
            <div className="space-y-16">
              {displayCategories
                .filter(cat => !selectedCategory || cat.category_id === selectedCategory)
                .map((category) => {
                  const categoryProducts = getProductsForCategory(category.category_id);
                  
                  return (
                    <div key={category.category_id} id={category.slug} className="scroll-mt-40">
                      {/* Category Header */}
                      <div className="flex flex-col md:flex-row gap-6 mb-8 items-start">
                        <div className="w-full md:w-1/3 lg:w-1/4">
                          <div className="relative rounded-xl overflow-hidden shadow-lg aspect-[4/3]">
                            <img 
                              src={category.image_url} 
                              alt={category.name}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#0B2C4D]/80 to-transparent" />
                            <div className="absolute bottom-0 left-0 right-0 p-4">
                              <h2 className="text-2xl font-bold text-white">{category.name}</h2>
                            </div>
                          </div>
                        </div>
                        <div className="flex-1">
                          <p className="text-gray-600 mb-4">{category.description}</p>
                          <p className="text-sm text-gray-500 mb-4">
                            {categoryProducts.length} products available • Subject to sourcing confirmation
                          </p>
                        </div>
                      </div>

                      {/* Products Grid */}
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                        {categoryProducts.map((product, idx) => (
                          <div 
                            key={product.product_id || idx}
                            className="bg-white rounded-lg shadow hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100 group"
                          >
                            <div className="relative h-36 overflow-hidden">
                              <img 
                                src={product.image_url || category.image_url}
                                alt={product.name}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              />
                            </div>
                            <div className="p-3">
                              <h3 className="text-sm font-semibold text-[#0B2C4D] mb-2 line-clamp-2 min-h-[2.5rem]">
                                {product.name}
                              </h3>
                              <Button
                                size="sm"
                                className="w-full bg-[#C9A24D] hover:bg-[#b8923e] text-[#0B2C4D] font-semibold text-xs"
                                onClick={() => openEnquiryForm(product)}
                                data-testid={`enquire-${product.product_id || idx}`}
                              >
                                <Send className="w-3 h-3 mr-1" />
                                Enquire Now
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </section>

      {/* Markets Section */}
      <section 
        className="py-20 relative"
        style={{
          backgroundImage: `linear-gradient(rgba(11, 44, 77, 0.92), rgba(11, 44, 77, 0.92)), url('https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=1920')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="container mx-auto px-6 text-white">
          <div className="text-center mb-12">
            <Globe className="w-20 h-20 text-[#C9A24D] mx-auto mb-6" />
            <h2 className="text-5xl font-bold mb-4">Markets We Serve</h2>
            <p className="text-xl text-gray-200">Connecting Indian excellence to global markets</p>
          </div>
          
          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4 max-w-7xl mx-auto">
            {markets.map((market, index) => (
              <div 
                key={index}
                className="bg-white bg-opacity-10 backdrop-blur-sm p-4 rounded-lg border-2 border-[#C9A24D] hover:bg-opacity-20 transition-all duration-300 flex items-center gap-4"
              >
                <img 
                  src={`https://flagcdn.com/w80/${market.code}.png`}
                  alt={market.name}
                  className="w-12 h-8 object-cover rounded shadow-md"
                />
                <p className="text-base font-semibold flex-1">{market.name}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button 
              size="lg"
              className="bg-[#C9A24D] hover:bg-[#b8923e] text-[#0B2C4D] font-semibold text-lg px-10 py-6"
              onClick={() => navigate('/contact')}
            >
              <Send className="w-5 h-5 mr-2" />
              Start Exporting Now
            </Button>
          </div>
        </div>
      </section>

      <Footer />

      {/* Enquiry Form Dialog */}
      <Dialog open={isEnquiryOpen} onOpenChange={setIsEnquiryOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#0B2C4D] text-xl">
              Product Enquiry
              {selectedProduct && (
                <span className="block text-sm font-normal text-gray-600 mt-1">
                  {selectedProduct.name}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEnquirySubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Your Name *</label>
                <Input
                  value={enquiryForm.full_name}
                  onChange={(e) => setEnquiryForm({...enquiryForm, full_name: e.target.value})}
                  required
                  placeholder="Full name"
                  data-testid="enquiry-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
                <Input
                  value={enquiryForm.company_name}
                  onChange={(e) => setEnquiryForm({...enquiryForm, company_name: e.target.value})}
                  required
                  placeholder="Company name"
                  data-testid="enquiry-company"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <Input
                  type="email"
                  value={enquiryForm.email}
                  onChange={(e) => setEnquiryForm({...enquiryForm, email: e.target.value})}
                  required
                  placeholder="your@email.com"
                  data-testid="enquiry-email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Country *</label>
                <Input
                  value={enquiryForm.country}
                  onChange={(e) => setEnquiryForm({...enquiryForm, country: e.target.value})}
                  required
                  placeholder="Your country"
                  data-testid="enquiry-country"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                <Input
                  value={enquiryForm.phone}
                  onChange={(e) => setEnquiryForm({...enquiryForm, phone: e.target.value})}
                  required
                  placeholder="+91 XXXXX XXXXX"
                  data-testid="enquiry-phone"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Required Quantity</label>
                <Input
                  value={enquiryForm.quantity}
                  onChange={(e) => setEnquiryForm({...enquiryForm, quantity: e.target.value})}
                  placeholder="e.g., 500 kg, 1 container"
                  data-testid="enquiry-quantity"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
              <Textarea
                value={enquiryForm.message}
                onChange={(e) => setEnquiryForm({...enquiryForm, message: e.target.value})}
                placeholder="Tell us about your requirements..."
                rows={3}
                data-testid="enquiry-message"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full bg-[#C9A24D] hover:bg-[#b8923e] text-[#0B2C4D] font-semibold"
              disabled={submitting}
              data-testid="enquiry-submit-btn"
            >
              {submitting ? 'Submitting...' : 'Submit Enquiry'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Products;
